/**
 * @flow
 */

import React, { useEffect, useMemo, useCallback } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';
import { format, startOfDay } from 'date-fns';
import { each, map, uniqBy, property, cloneDeep, findIndex, remove } from 'lodash';

import { eachDay } from '../../../lib/date';
import Timetable from './timetable/Timetable';

import {
    GET_MASTER_APPOINTMENTS_QUERY,
    APPOINTMENT_ADD_SUBSCRIPTION,
    APPOINTMENT_DELETE_SUBSCRIPTION,
} from '../containers/queries';

import type {
    Salon,
    Master,
    Appointment,
    ScheduleSettings,
} from '../../../type';

export type TimetableDay = {
    date: string,
    appointments: Array<Appointment>,
    schedule: Array<ScheduleSettings>,
};

function AppointmentTable() {
    const apolloClient = useApolloClient();
    const salonId = useSelector(({ user }) => user.get('salon'));
    const fromDate = useSelector(({ masterAppointments }) =>
        masterAppointments.get('fromDate'),
    );
    const toDate = useSelector(({ masterAppointments }) =>
        masterAppointments.get('toDate'),
    );
    const master = useSelector(
        ({ masterAppointments }) => masterAppointments.get('master') || {},
    );
    const showArchive = useSelector(({ salon }) => salon.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const variables = {
        master: master.id,
        withMaster: !!master.id,
        salon: salonId,
        fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
        toDate: format(new Date(toDate), 'yyyy-MM-dd'),
        showArchive,
        lang: currentLanguage,
    };

    const { data, loading, subscribeToMore } = useQuery(
        GET_MASTER_APPOINTMENTS_QUERY,
        {
            variables,
            fetchPolicy: 'cache-and-network',
            skip: !salonId || !master.id,
        },
    );

    // Subscriptions - resubscribe when salon/dates change
    useEffect(() => {
        if (!salonId || !master.id) return;

        const dateFormat = 'yyyy-MM-dd';
        const subVars = {
            salon: salonId,
            date: format(new Date(fromDate), dateFormat),
            toDate: format(new Date(toDate), dateFormat),
            lang: currentLanguage,
        };

        const unsubAdd = subscribeToMore({
            document: APPOINTMENT_ADD_SUBSCRIPTION,
            variables: subVars,
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) return prev;
                const next = cloneDeep(prev);
                const appointment = subscriptionData.data.appointmentAdded;
                const masterData = next.viewer.salon.master;
                if (
                    (showArchive || !appointment.archived) &&
                    masterData &&
                    masterData.id === appointment.master.id
                ) {
                    const oldIdx = findIndex(
                        masterData.appointments,
                        o => o.id === appointment.id,
                    );
                    if (oldIdx !== -1) {
                        masterData.appointments[oldIdx] = appointment;
                    } else {
                        masterData.appointments.push(appointment);
                    }
                }
                return next;
            },
        });

        const unsubDelete = subscribeToMore({
            document: APPOINTMENT_DELETE_SUBSCRIPTION,
            variables: subVars,
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) return prev;
                const next = cloneDeep(prev);
                const appointment = subscriptionData.data.appointmentDeleted;
                const masterData = next.viewer.salon.master;
                if (masterData && masterData.id === appointment.master.id) {
                    remove(masterData.appointments, o => o.id === appointment.id);
                }
                return next;
            },
        });

        return () => {
            unsubAdd();
            unsubDelete();
        };
    }, [salonId, master.id, fromDate, toDate, showArchive, currentLanguage, subscribeToMore]); // eslint-disable-line react-hooks/exhaustive-deps

    // Derive days from query data (same logic as old container)
    const { days, masterData, salon } = useMemo(() => {
        const dateFormat = 'yyyy-MM-dd';
        const dateRange = eachDay(fromDate, toDate, dateFormat);
        const dayList = map(dateRange, date => ({
            date,
            appointments: [],
            schedule: [],
        }));
        let masterResult = {};
        const salonResult = data && data.viewer && data.viewer.salon;
        if (!loading && salonResult && salonResult.master) {
            masterResult = salonResult.master;
            each(masterResult.appointments, appointment => {
                const idx = dateRange.indexOf(
                    format(new Date(appointment.startAt), dateFormat),
                );
                if (dayList[idx]) dayList[idx].appointments.push(appointment);
            });
            each(masterResult.schedule, day => {
                const idx = dateRange.indexOf(day.date);
                if (dayList[idx]) dayList[idx].schedule.push(day);
            });
        }
        return { days: dayList, masterData: masterResult, salon: salonResult };
    }, [data, loading, fromDate, toDate]);

    const services = useMemo(
        () =>
            uniqBy(
                map(
                    (masterData && masterData.appointments) || [],
                    property('service'),
                ),
                property('id'),
            ),
        [masterData],
    );

    const updateAppointmentClient = useCallback(
        client => {
            const queryVars = {
                master: master.id,
                withMaster: !!master.id,
                salon: salonId,
                fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                showArchive,
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_MASTER_APPOINTMENTS_QUERY,
                variables: queryVars,
            });
            if (!cacheData) return;
            const appointments =
                cacheData.viewer.salon.master.appointments || [];
            const nextAppointments = appointments.map(appt => {
                if (appt.client.id === client.id) {
                    return { ...appt, notes: client.notes };
                }
                return appt;
            });
            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        master: {
                            ...cacheData.viewer.salon.master,
                            appointments: nextAppointments,
                        },
                    },
                },
            };
            apolloClient.cache.writeQuery({
                query: GET_MASTER_APPOINTMENTS_QUERY,
                variables: queryVars,
                data: nextData,
            });
        },
        [master.id, salonId, fromDate, toDate, showArchive, currentLanguage, apolloClient], // eslint-disable-line react-hooks/exhaustive-deps
    );

    if (!salon && loading) {
        return <Loader size="big" inline="centered" active />;
    }

    if (!salon) return null;

    return (
        <Timetable
            loading={loading}
            schedule={salon.settings.schedule}
            date={startOfDay(new Date(fromDate))}
            services={services}
            days={days}
            master={masterData}
            salon={salon}
            updateAppointmentClient={updateAppointmentClient}
        />
    );
}

export default AppointmentTable;
